/**
 * YAC-2026-009 PR5a。運営の2段階認証の失敗を、画面の振る舞いに分ける。
 *
 * サーバーの約束（api/mfa_views.py・#384）:
 *  - 401 mfa_challenge_invalid・mfa_attempts_exceeded・account_locked・invitation_expired、
 *    409 mfa_wrong_step: そのチャレンジは使えない → パスワードの画面へ戻す
 *  - 400 mfa_code_invalid: コードの誤り（ロックにも数える）
 *  - 429: 試行の制限。mfa_email_resend_too_soon は送り直しの間隔、mfa_email_resend_limit は送り直しの上限
 *  - 502 email_not_sent: メールを送れなかった（チャレンジは失効）
 *  - 503 mfa_unavailable: 秘密を読めない（運用で登録し直す）
 */
import { MfaAbortedError, UnexpectedMfaResponseError } from 'config/axiosInstance';

export type MfaFailure =
  /** 画面がやめた試行（#385 の指摘 4368093977）。何もしない。 */
  | { kind: 'aborted' }
  /** パスワードの画面へ戻す。message は login の名前空間の鍵。 */
  | { kind: 'restart'; message: string }
  | { kind: 'code_invalid' }
  /** 待つ。seconds は Retry-After（読めなければ null）。 */
  | { kind: 'throttled'; seconds: number | null }
  | { kind: 'resend_too_soon'; seconds: number | null }
  | { kind: 'resend_limit' }
  /** パスワードの検査で断られた（サーバーの文。要求の言語で返る）。 */
  | { kind: 'password_rejected'; messages: string[] }
  | { kind: 'network' }
  | { kind: 'origin_not_allowed' };

/** Retry-After（秒、または HTTP の日時）を秒にする。読めなければ null。 */
export function retryAfterSeconds(error: any, now: number = Date.now()): number | null {
  const headers = error?.response?.headers;
  const raw =
    headers && typeof headers.get === 'function'
      ? headers.get('retry-after')
      : headers?.['retry-after'] ?? headers?.['Retry-After'];
  if (raw === undefined || raw === null || raw === '') return null;
  const text = String(raw).trim();
  if (/^\d+$/.test(text)) return Math.max(1, Number(text));
  const at = Date.parse(text);
  if (Number.isNaN(at)) return null;
  return Math.max(1, Math.ceil((at - now) / 1000));
}

function passwordMessages(data: any): string[] {
  const value = data?.password;
  if (Array.isArray(value)) return value.filter((item) => typeof item === 'string');
  if (typeof value === 'string') return [value];
  return [];
}

export function classifyMfaError(error: any): MfaFailure {
  if (error instanceof MfaAbortedError) return { kind: 'aborted' };
  if (error instanceof UnexpectedMfaResponseError) {
    return { kind: 'restart', message: 'mfa.error-unexpected' };
  }
  const response = error?.response;
  if (!response) {
    return error?.isAxiosError ? { kind: 'network' } : { kind: 'restart', message: 'mfa.error-unexpected' };
  }
  const status = response.status;
  const code = response.data?.code;
  if (status === 400) {
    if (code === 'mfa_code_invalid') return { kind: 'code_invalid' };
    const messages = passwordMessages(response.data);
    if (messages.length > 0) return { kind: 'password_rejected', messages };
    // コードの形の誤り（数字6桁でない）などは、コードの誤りと同じに扱う。
    return { kind: 'code_invalid' };
  }
  if (status === 429) {
    if (code === 'mfa_email_resend_limit') return { kind: 'resend_limit' };
    const seconds = retryAfterSeconds(error);
    if (code === 'mfa_email_resend_too_soon') return { kind: 'resend_too_soon', seconds };
    return { kind: 'throttled', seconds };
  }
  if (status === 403 && code === 'csrf_failed') return { kind: 'origin_not_allowed' };
  if (status === 401 || status === 409) {
    if (code === 'mfa_attempts_exceeded') return { kind: 'restart', message: 'mfa.error-attempts' };
    if (code === 'account_locked' || code === 'account_locked_contact_admin') {
      return { kind: 'restart', message: 'mfa.error-locked' };
    }
    if (code === 'invitation_expired') return { kind: 'restart', message: 'mfa.error-invitation-expired' };
    return { kind: 'restart', message: 'mfa.error-expired' };
  }
  if (status === 502 && code === 'email_not_sent') {
    return { kind: 'restart', message: 'mfa.error-email-not-sent' };
  }
  if (status === 503 && code === 'mfa_unavailable') {
    return { kind: 'restart', message: 'mfa.error-unavailable' };
  }
  return { kind: 'restart', message: 'mfa.error-unexpected' };
}
